import { useQuery } from "@tanstack/react-query";
import { differenceInYears, parseISO } from "date-fns";
import { supabase } from "@/lib/supabase";

// ============================================================================
// AI-ML-01 customer-metrics integration for Customer 360.
//
// Reads from customer_metrics_proposed (the *_proposed table holds the most
// recent nightly computation; live promotion happens via SET-18 Settings ->
// Metrics Review per DEC-20 + AI-ML-01 design).
//
// This file owns: the row type, the React Query hooks (customer metrics +
// tech-name lookup), and the pure prose builder used by CustomerProseSummary.
//
// Schema lives in C:\ESCBACKUP\sql\ai-ml-01-schema.sql + ai-ml-01-functions.sql.
// ============================================================================

export interface CustomerMetricsRow {
  custno: string;
  total_dispatches: number | null;
  first_seen_at: string | null;
  /** Where first_seen_at came from: histled | dispatch | sales | date_added.
   * 'date_added' = the ESC record-creation date — NOT evidence of a customer
   * relationship (Jennifer Mueller false-"13-year customer", 2026-06-04). */
  first_seen_at_source: string | null;
  last_seen_at: string | null;
  lifetime_revenue_cents: number | null;
  tenure_data_quality: "clean" | "wintac_legacy" | "chimera_suspected" | null;
}

/**
 * Fetch the AI-ML-01-computed metrics row for one customer.
 * Returns null when the customer has no metrics row yet (brand-new customer
 * or one that didn't qualify in the last nightly run).
 *
 * Note: preferred_tech / preferred_window are no longer consumed by the v1
 * prose (per Seth's "statistical vs stated" pushback 2026-05-25). The columns
 * still exist on the proposed table for future surfaces, but the prose only
 * uses the durable counts + tenure facts.
 */
export function useCustomerMetrics(custno: string | undefined) {
  return useQuery({
    queryKey: ["customer_metrics", custno],
    queryFn: async (): Promise<CustomerMetricsRow | null> => {
      if (!custno) return null;
      const { data, error } = await supabase
        .from("customer_metrics_proposed")
        .select(
          "custno, total_dispatches, first_seen_at, first_seen_at_source, " +
            "last_seen_at, lifetime_revenue_cents, tenure_data_quality",
        )
        .eq("custno", custno)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as CustomerMetricsRow) ?? null;
    },
    enabled: !!custno,
    // Metrics recompute nightly; a 5-min cache is plenty for one UI session.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * One-shot lookup of empno -> friendly tech name. Pulls all employees once
 * (there are ~100, they barely change) and returns a Map.
 *
 * Currently unused by the v1 prose; kept here for future surfaces (BUILD-13b
 * "Recent service" chip-row, MIG-NOTES-01 stated-preference rendering, etc.)
 * that will need to render "Brent" instead of "0008".
 */
export function useTechNameLookup() {
  return useQuery({
    queryKey: ["tech_name_lookup"],
    queryFn: async (): Promise<Map<string, string>> => {
      const { data, error } = await supabase
        .from("mirror_employee")
        .select("empno, empname, firstname, lastname");
      if (error) throw error;
      const map = new Map<string, string>();
      for (const row of (data ?? []) as {
        empno: string;
        empname: string | null;
        firstname: string | null;
        lastname: string | null;
      }[]) {
        const name =
          row.firstname?.trim() ||
          row.empname?.trim() ||
          row.lastname?.trim() ||
          `Tech ${row.empno}`;
        map.set(row.empno, name);
      }
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ----------------------------------------------------------------------------
// Prose builder — pure function, no React, easy to unit-test later.
// ----------------------------------------------------------------------------

export interface ProseResult {
  text: string;
  isChimera: boolean;
}

interface ProseBuilderArgs {
  metrics: CustomerMetricsRow | null;
  locationCount: number;
}

// Property-manager phrasing kicks in at this many locations. Below this,
// multi-location customers (a few houses owned by one person) read as
// regular customers.
const PROPERTY_MANAGER_THRESHOLD = 5;

/**
 * Build the one-line Customer 360 prose summary from AI-ML-01 metrics.
 *
 * Real examples from 2026-05-25 spot-check:
 *   Moller & Barrett:    "24-year customer · 34 dispatches · $22K lifetime"
 *   Highland Mgmt Group: "23-year property manager · 90 properties · 658 dispatches · $1.8M lifetime"
 *   Phil MacDougall:     "Recent buyer at this property"
 *
 * Returns null when there's nothing meaningful to say (no metrics row, or
 * brand-new customer with no dispatches and no revenue yet).
 *
 * CHIMERA SHORT-CIRCUIT (locked 2026-05-25 per Seth):
 * When tenure_data_quality = 'chimera_suspected', we surface ONLY the
 * relationship clause ("Recent buyer at this property"). No dispatch
 * count, no lifetime revenue. Those numbers were accumulated by ESC under
 * this custno but the history may belong to a prior owner whose record
 * was edited in-place when the property sold. Showing inherited numbers
 * as if they belong to the current customer contradicts the chimera flag
 * itself. When BUILD-17 ships DEC-NEW-04 ownership history, dispatches
 * get re-attributed to the current owner's tenure and the prose can
 * honestly show their actual counts.
 *
 * STATISTICAL VS STATED (locked 2026-05-25 per Seth):
 * preferred_tech / preferred_window are NOT surfaced in v1 prose. The
 * algorithm only knows statistical pattern ("most often with X"); the
 * label "preferred" implied a stated preference we can't yet detect.
 * That signal comes back when MIG-NOTES-01 extracts stated preferences
 * from location notes — at which point it gets its own clause with
 * appropriate vocabulary distinction.
 */
export function buildCustomerProse(args: ProseBuilderArgs): ProseResult | null {
  const { metrics, locationCount } = args;
  if (!metrics) return null;

  const isChimera = metrics.tenure_data_quality === "chimera_suspected";

  // Chimera short-circuit — relationship clause only, no inherited numbers.
  if (isChimera) {
    return { text: "Recent buyer at this property", isChimera: true };
  }

  // Clean / wintac_legacy path — tenure + dispatches + revenue.
  const parts: string[] = [];
  let tenureClauseAdded = false;

  const dispCount = metrics.total_dispatches ?? 0;
  const revenueCents = metrics.lifetime_revenue_cents ?? 0;

  // NO-ACTIVITY GUARD (Seth, 2026-06-04 — Jennifer Mueller): a record with
  // ZERO dispatches and ZERO revenue is not a "13-year customer" no matter
  // how old its row is — first_seen may be just the ESC data-entry date
  // (first_seen_at_source='date_added': a quote that never closed, a caller
  // who never booked). Say what we actually know, with the context.
  if (dispCount === 0 && revenueCents === 0) {
    if (!metrics.first_seen_at) return null;
    const year = metrics.first_seen_at.slice(0, 4);
    return {
      text: `In the system since ${year} — no service history on record`,
      isChimera: false,
    };
  }

  if (metrics.first_seen_at) {
    const years = differenceInYears(
      new Date(),
      parseISO(metrics.first_seen_at),
    );
    if (locationCount >= PROPERTY_MANAGER_THRESHOLD) {
      parts.push(
        `${years}-year property manager · ${locationCount} properties`,
      );
      tenureClauseAdded = true;
    } else if (years >= 1) {
      parts.push(`${years}-year customer`);
      tenureClauseAdded = true;
    }
  }

  if (dispCount > 0) {
    parts.push(`${dispCount} ${dispCount === 1 ? "dispatch" : "dispatches"}`);
  }

  if (revenueCents > 0) {
    parts.push(`${formatCentsAsHuman(revenueCents)} lifetime`);
  }

  // Render nothing for a record with no story (no tenure clause AND no
  // dispatches AND no revenue) rather than emitting an awkward single phrase.
  if (!tenureClauseAdded && dispCount === 0 && revenueCents === 0) {
    return null;
  }
  if (parts.length === 0) return null;

  return { text: parts.join(" · "), isChimera: false };
}

/**
 * Format a cents value as a human-friendly money string.
 *   2723       -> "$27"
 *   272300     -> "$2.7K"
 *   2220800    -> "$22K"
 *   181922100  -> "$1.8M"
 */
export function formatCentsAsHuman(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 10_000) return `$${Math.round(dollars / 1000)}K`;
  if (dollars >= 1000) return `$${(dollars / 1000).toFixed(1)}K`;
  return `$${Math.round(dollars)}`;
}
